/* 
Component: Navigation Header, only shown for Authenticated Users
*/

import { Box, Button, DropButton, Header, Nav, Text } from "grommet";
import React, { useState } from "react";
import logo from "../monitoredu-logo.svg";
import { User } from "grommet-icons";
import { useNavigate } from "react-router-dom";
import { useUser } from "../AuthContext";
import { Auth } from "aws-amplify";

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function getDisplayProctorName(username, email) {
  const name = (username || "").trim();
  if (!name) return "";
  if (email && name.toLowerCase() === email.toLowerCase()) return "";
  if (UUID_PATTERN.test(name)) return "";
  return name;
}

function capitalizeDisplayName(name) {
  return String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

function Navigator() {
  let navigate = useNavigate(); //used to navigate to different pages
  const myUser = useUser(); //user object from AuthContext
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userEmail = myUser?.user?.attributes?.email || "";
  const displayName = getDisplayProctorName(myUser?.proctorName, userEmail);
  const greetingName = capitalizeDisplayName(displayName) || "Proctor";
  const greeting = `Hello ${greetingName}!`;

  const closeUserMenu = () => setUserMenuOpen(false);

  const handleUserMenuNavigate = (path) => {
    closeUserMenu();
    navigate(path);
  };

  const handleSignOut = () => {
    closeUserMenu();
    Auth.signOut();
  };

  return (
    <Box overflow="auto" align="center" flex="grow" fill responsive gap="none">
      <Header
        align="center"
        direction="row"
        flex={false}
        justify="between"
        gap="medium"
        fill="horizontal"
        background="transparent"
        overflow="auto"
      >
        <Box
          align="center"
          justify="center"
          height="xxsmall"
          responsive
          pad={{
            top: "medium",
            horizontal: "medium",
            bottom: "small",
          }}
          wrap
        >
          <img
            src={logo}
            alt="logo"
            style={{
              width: "200px",
              maxWidth: "100%",
              height: "auto",
            }}
          ></img>
        </Box>
        <Box
          align="center"
          justify="center"
          responsive
          wrap={false}
          overflow="auto"
        >
          <Nav
            align="center"
            flex
            direction="row"
            margin={{ right: "medium" }}
            gap="none"
            overflow="auto"
            pad="xsmall"
          >
            <Button
              label="Pod Sessions"
              href="/pod-queue"
              onClick={(event) => {
                // Cmd/Ctrl/Shift/middle-click opens href in a new tab.
                // Do not also route the current tab.
                if (
                  event.metaKey ||
                  event.ctrlKey ||
                  event.shiftKey ||
                  event.altKey ||
                  event.button !== 0
                ) {
                  return;
                }
                event.preventDefault();
                navigate("/pod-queue");
              }}
            />
            <Button
              label="Mobile"
              onClick={() => navigate("/twmobile")}
              href="/twmobile"
            />
            <DropButton
              label="Webcams"
              dropAlign={{ top: "bottom" }}
              dropContent={
                <Box
                  align="stretch"
                  justify="center"
                  pad="xsmall"
                  name="dropContent"
                  width="small"
                >
                  <Button
                    label="Twilio Rooms"
                    onClick={() => navigate("/twiliosc")}
                    href="/twiliosc"
                  />
                  <Button
                    label="Twilio Link Form"
                    onClick={() => navigate("/twiliolinks")}
                    href="/twiliolinks"
                  />
                  <Button
                    label="Twilio Users"
                    onClick={() => navigate("/twiliousers")}
                    href="/twiliousers"
                  />
                  {/*
                  <Button
                    label="Webcam Rooms"
                    onClick={() => navigate("/webcams")}
                    href="/webcams"
                  />
                  <Button
                    label="Webcam Link Form"
                    onClick={() => navigate("/webcam-form")}
                    href="/webcam-form"
                  />
                  <Button
                    label="Webcam Users"
                    onClick={() => navigate("/webcam-users")}
                    href="/webcam-users"
                  />
              */}
                </Box>
              }
              dropProps={{ elevation: "xsmall", plain: false }}
              primary={false}
            />
            <DropButton
              label="Integrations"
              dropAlign={{ top: "bottom" }}
              dropContent={
                <Box
                  align="stretch"
                  justify="center"
                  pad="xsmall"
                  name="dropContent"
                  width="small"
                >
                  <Button
                    label="AAA Food Handlers"
                    onClick={() => navigate("/aaa-food-handlers")}
                    href="/aaa-food-handlers"
                  />
                  <Button
                    label="Backup Queue"
                    onClick={() => navigate("/proctorbackupqueue")}
                    href="/proctorbackupqueue"
                  />
                  <Button
                    label="CMS | ItemExperts"
                    onClick={() => navigate("/cms")}
                    href="/cms"
                  />
                  <Button
                    label="Colibri Proctor Form"
                    href="https://3520739.extforms.netsuite.com/app/site/hosting/scriptlet.nl?script=1206&deploy=1&compid=3520739&ns-at=AAEJ7tMQGVyp82liM-vfHyYd-L8hKOrYC9qzGs8xsc2LL3ZShlA"
                    target="_blank"
                  />
                  <Button
                    label="CRANE"
                    onClick={() => navigate("/crane")}
                    href="/crane"
                  />
                  <Button
                    label="Exam Codes"
                    onClick={() => navigate("/codes")}
                    href="/codes"
                  />
                  <Button
                    label="Extension Sessions"
                    onClick={() => navigate("/extension")}
                    href="/extension"
                  />
                  <Button
                    label="Google Rooms"
                    onClick={() => navigate("/google-rooms")}
                    href="/google-rooms"
                  />
                  <Button
                    label="Invigil"
                    onClick={() => navigate("/invigil")}
                    href="/invigil"
                  />
                  <Button
                    label="Payment Validation"
                    onClick={() => navigate("/payment-validation")}
                    href="/payment-validation"
                  />
                  <Button
                    label="Prolydian"
                    onClick={() => navigate("/prolydian-integration")}
                    href="/prolydian-integration"
                  />
                </Box>
              }
              dropProps={{ elevation: "xsmall", plain: false }}
              primary={false}
            />
            <DropButton
              a11yTitle="User menu"
              open={userMenuOpen}
              onOpen={() => setUserMenuOpen(true)}
              onClose={closeUserMenu}
              dropAlign={{ top: "bottom", right: "right" }}
              dropContent={
                <Box
                  align="stretch"
                  justify="center"
                  pad="xsmall"
                  name="dropContent"
                  width="240px"
                  round="small"
                >
                  <Box
                    align="start"
                    pad={{ horizontal: "xsmall", vertical: "xxsmall" }}
                    margin={{ bottom: "medium" }}
                  >
                    <Text weight="bold">{greeting}</Text>
                    <Text
                      size="small"
                      color="text-weak"
                      truncate
                      title={userEmail || undefined}
                    >
                      {userEmail}
                    </Text>
                  </Box>
                  <Button
                    plain
                    fill="horizontal"
                    hoverIndicator
                    onClick={() => handleUserMenuNavigate("/proctorprofile")}
                  >
                    <Box
                      fill="horizontal"
                      align="start"
                      pad={{ horizontal: "xsmall", vertical: "xsmall" }}
                    >
                      <Text>Proctor Profile</Text>
                    </Box>
                  </Button>
                  <Button
                    plain
                    fill="horizontal"
                    hoverIndicator
                    onClick={() => handleUserMenuNavigate("/session-history")}
                  >
                    <Box
                      fill="horizontal"
                      align="start"
                      pad={{ horizontal: "xsmall", vertical: "xsmall" }}
                    >
                      <Text>Session History</Text>
                    </Box>
                  </Button>
                  <Box
                    margin={{ top: "medium" }}
                    border={{ side: "top", color: "border" }}
                    pad={{ top: "small" }}
                  >
                    <Button
                      plain
                      fill="horizontal"
                      hoverIndicator
                      onClick={handleSignOut}
                    >
                      <Box
                        fill="horizontal"
                        align="start"
                        pad={{ horizontal: "xsmall", vertical: "xsmall" }}
                      >
                        <Text color="#b92b37">Sign Out</Text>
                      </Box>
                    </Button>
                  </Box>
                </Box>
              }
              dropProps={{ elevation: "xsmall", plain: false, round: "small" }}
              primary={false}
              icon={<User />}
              size="small"
            />
          </Nav>
        </Box>
      </Header>
    </Box>
  );
}

export default Navigator;
